import { Dispatch } from 'redux'
import { _axios } from '../../../api/service'
import { budgetApi } from '../../../api/budgetApi'
import {
  fetchBudgetDetailStart,
  fetchBudgetDetailSuccess,
  fetchBudgetDetailFailure,
  toggleEditMode as toggleEditModeReducer,
  clearBudgetDetail as clearBudgetDetailReducer,
  markBudgetDetailAsStale,
  setBenchmarkData,
} from '../reducers/budgetDetailReducer'
import { IStudyBudget, IBenchmarkFilters } from '../types/studyBudget'
import { RootState } from '../../../store/rootState'
import { extractErrorMessage } from '../../../utils/errorHandler'
import { restoreDeletedPosition } from '../reducers/budgetDetailReducer'
import { DeletedPosition } from '../utils/amendmentDiff'
import { trackChange } from '../slices/changeTrackerSlice'

/**
 * Clean up old snapshot-based quantity overrides
 * Converts deprecated { value, calculation_figure_snapshot/phase_dates_snapshot } format
 * to simple { value } format
 */
const cleanupQuantityOverrides = (budgetData: IStudyBudget): IStudyBudget => {
  const cleanedData = { ...budgetData }

  // Clean budget_positions
  if (cleanedData.budget_positions) {
    cleanedData.budget_positions = cleanedData.budget_positions.map((category: any) => ({
      ...category,
      department_roles: (category.department_roles || []).map((role: any) => {
        const cleanedRole = { ...role }

        if (cleanedRole.quantity_override && typeof cleanedRole.quantity_override === 'object') {
          if ('calculation_figure_snapshot' in cleanedRole.quantity_override ||
              'phase_dates_snapshot' in cleanedRole.quantity_override) {
            cleanedRole.quantity_override = {
              value: cleanedRole.quantity_override.value
            }
          }
        }

        return cleanedRole
      })
    }))
  }

  // Clean external_positions
  if (cleanedData.external_positions) {
    cleanedData.external_positions = cleanedData.external_positions.map((position: any) => {
      const cleanedPosition = { ...position }

      if (cleanedPosition.quantity_override && typeof cleanedPosition.quantity_override === 'object') {
        if ('calculation_figure_snapshot' in cleanedPosition.quantity_override ||
            'phase_dates_snapshot' in cleanedPosition.quantity_override) {
          cleanedPosition.quantity_override = {
            value: cleanedPosition.quantity_override.value
          }
        }
      }

      return cleanedPosition
    })
  }

  return cleanedData
}

/**
 * Fetch budget detail by ID
 */
export const fetchBudgetDetail = (budgetId: string, forceRefresh = false) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState()
  const currentBudget = state.budgetDetail?.budget
  const isStale = state.budgetDetail?.stale

  // Check if we already have this budget and it's not stale (unless force refresh is requested)
  if (!forceRefresh && currentBudget && currentBudget.id?.toString() === budgetId && !isStale) {
    // Data is already cached and fresh, no need to fetch
    return
  }

  dispatch(fetchBudgetDetailStart())

  try {
    const response = await _axios.get(`/api/budget-module/projects/${budgetId}/`)

    // Clean up old snapshot-based overrides before storing in Redux
    const budgetData: IStudyBudget = cleanupQuantityOverrides(response.data)
    dispatch(fetchBudgetDetailSuccess(budgetData))
  } catch (error: any) {
    const errorMessage = error.response?.status === 404
      ? 'Budget not found'
      : extractErrorMessage(error)
    dispatch(fetchBudgetDetailFailure(errorMessage))
  }
}

/**
 * Toggle edit mode
 */
export const toggleEditMode = () => (dispatch: Dispatch) => {
  dispatch(toggleEditModeReducer())
}

/**
 * Clear budget detail from state
 */
export const clearBudgetDetail = () => (dispatch: Dispatch) => {
  dispatch(clearBudgetDetailReducer())
}

/**
 * Mark budget detail as stale to force refresh
 */
export const markBudgetDetailStale = () => (dispatch: Dispatch) => {
  dispatch(markBudgetDetailAsStale())
}

/**
 * Update study data (study title, client, therapeutic area, etc.)
 */
export const updateStudyData = (budgetId: string, studyData: any) => async (dispatch: any) => {
  try {
    // Map frontend field names to backend field names
    const payload = {
      name: studyData.studyTitle,
      client: studyData.client,
      therapeutic_area: studyData.therapeuticArea,
      study_type: studyData.studyType,
      study_phase: studyData.studyPhase
    }
    
    const response = await _axios.patch(`/api/budget-module/projects/${budgetId}/`, payload)
    
    // Force refresh budget detail to sync all components
    await dispatch(fetchBudgetDetail(budgetId, true))
    
    return response.data
  } catch (error: any) {
    console.error('Failed to update study data:', error)
    throw error
  }
}

/**
 * Export budget positions to CSV
 *
 * Triggers download of CSV file containing all budget positions.
 * Direct API call - no state updates needed.
 *
 * @param budgetId - Budget project ID to export
 */
export const exportBudgetCSV = (budgetId: string) => async (dispatch: Dispatch) => {
  try {
    await budgetApi.exportBudget(budgetId)
  } catch (error: any) {
    console.error('Failed to export budget:', error)
    const errorMessage = extractErrorMessage(error)
    // You could dispatch an error action here if needed
    throw new Error(`Export failed: ${errorMessage}`)
  }
}

/**
 * Export budget as PDF
 *
 * Triggers download of PDF file containing budget report.
 * Direct API call - no state updates needed.
 *
 * @param budgetId - Budget project ID to export
 */
export const exportBudgetPDF = (budgetId: string) => async (dispatch: Dispatch) => {
  try {
    await budgetApi.exportBudgetPDF(budgetId)
  } catch (error: any) {
    console.error('Failed to export budget PDF:', error)
    const errorMessage = extractErrorMessage(error)
    throw new Error(`PDF export failed: ${errorMessage}`)
  }
}

/**
 * Fetch benchmark data for a budget under the given cohort filters.
 * @param budgetId - Budget project ID
 * @param filters - Cohort filters (use DEFAULT_BENCHMARK_FILTERS for the unfiltered view)
 */
// Guards against out-of-order responses: rapid filter changes fire several fetches,
// and only the most recent one is allowed to update state — so a slow earlier
// response can't overwrite the latest cohort.
let benchmarkFetchToken = 0
export const fetchBenchmarks = (budgetId: number | string, filters: IBenchmarkFilters) => async (dispatch: Dispatch) => {
  const token = ++benchmarkFetchToken
  try {
    const data = await budgetApi.getBenchmarks(budgetId, filters)
    if (token === benchmarkFetchToken) dispatch(setBenchmarkData(data))
  } catch (error: any) {
    console.error('Failed to fetch benchmarks:', error)
    if (token === benchmarkFetchToken) dispatch(setBenchmarkData(null))
  }
}

// `fetchSourceBudget` used to live here and re-fetched the live source budget of an
// amendment so the detail page could diff against it. It is gone: the detail payload now
// carries `source_snapshot`, the source frozen at amendment creation, so the diff needs
// no second request and can no longer be re-based by a later edit to the source
// (issue #20 P0).
/**
 * Restore a saved-deleted position AND tell the change tracker about it.
 *
 * The reducer only puts the position back into `budgetDetail.budget`. That is not enough
 * on its own: the positions table renders `trackedChanges.budget_positions` in preference
 * to `budget.budget_positions`, and buildSavePayload sends the tracked array. So whenever
 * anything had already tracked a position change — the study-parameters panel does — a
 * bare restore would put the row into `budget` where the diff engine could see it (the
 * ghost row disappears, so it looks like it worked) while the table kept rendering the
 * stale tracked array and the save silently dropped the restored position.
 *
 * A thunk rather than a component callback because the updated positions have to be read
 * back out of the store after the reducer has run (issue #20 C4).
 *
 * @param deleted - the ghost row, straight from the diff engine's `deleted` list
 * @example
 * dispatch(restoreDeletedPositionAndTrack(ghostRow))
 */
export const restoreDeletedPositionAndTrack = (deleted: DeletedPosition) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(restoreDeletedPosition(deleted))

    const budget = getState().budgetDetail.budget!
    const field = deleted.kind === 'internal' ? 'budget_positions' : 'external_positions'
    dispatch(trackChange({ field, value: budget[field] }) as any)
  }
